import React, { useState } from 'react';
import FileUpload from './FileUpload';
import YouTubeVideoInput from './YouTubeVideoInput';
import EditItemModal from './EditItemModal';
import './FileUpload.css';

const GalleryManager = ({ value = [], onChange, type = 'mixed' }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  
  const handleFileUpload = (fileData) => {
    // Create gallery item from uploaded file (images only)
    const galleryItem = {
      id: Date.now().toString(),
      title: fileData.name.replace(/\.[^/.]+$/, ""), // Remove extension
      type: fileData.type,
      ...fileData
    };
    
    // Add icon for images
    galleryItem.icon = 'fas fa-camera';
    
    const newValue = [...value, galleryItem];
    onChange(newValue);
  };

  const handleYouTubeVideoAdd = (videoData) => {
    // YouTube video is already formatted by YouTubeVideoInput
    const newValue = [...value, videoData];
    onChange(newValue);
  };
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleItemUpdate = (index, field, newValue) => {
    const updatedItems = [...value];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: newValue
    };
    onChange(updatedItems);
  };

  const handleReplaceMedia = (index, fileData) => {
    const updatedItems = [...value];
    const existingItem = updatedItems[index];
    
    // Create new gallery item from uploaded file, preserving the title
    const newItem = {
      id: existingItem.id || Date.now().toString(),
      title: existingItem.title || fileData.name.replace(/\.[^/.]+$/, ""),
      type: fileData.type,
      ...fileData
    };
    
    // Add duration for videos, icon for images
    if (fileData.type === 'video') {
      newItem.duration = formatDuration(fileData.duration || 0);
      newItem.icon = 'fas fa-play-circle';
    } else {
      newItem.icon = 'fas fa-camera';
    }
    
    updatedItems[index] = newItem;
    onChange(updatedItems);
  };

  const isLegacyItem = (item) => {
    // Legacy items only have title, icon, and maybe duration - no data or thumbnail
    return !item.data && !item.thumbnail && !item.type;
  };

  const handleEditItem = (index) => {
    setEditingItem({ item: value[index], index });
  };

  const handleSaveEditedItem = (index, editedItem) => {
    const updatedItems = [...value];
    updatedItems[index] = editedItem;
    onChange(updatedItems);
    setEditingItem(null);
  };

  const handleCloseEditModal = () => {
    setEditingItem(null);
  };
  
  const handleItemDelete = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      const updatedItems = value.filter((_, i) => i !== index);
      onChange(updatedItems);
    }
  };
  
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedItems.length} élément(s) ?`)) {
      const updatedItems = value.filter((_, index) => !selectedItems.includes(index));
      onChange(updatedItems);
      setSelectedItems([]);
    }
  };
  
  const toggleItemSelection = (index) => {
    setSelectedItems(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  
  const selectAllItems = () => {
    if (selectedItems.length === value.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(value.map((_, index) => index));
    }
  };
  
  const getFileTypeFilter = () => {
    switch (type) {
      case 'photos':
        return 'image/*';
      case 'videos':
        return 'video/*';
      default:
        return 'image/*,video/*';
    }
  };
  
  const getUploadLabel = () => {
    switch (type) {
      case 'photos':
        return 'Télécharger des photos';
      case 'videos':
        return 'Télécharger des vidéos';
      default:
        return 'Télécharger des médias';
    }
  };

  return (
    <div className="gallery-manager">
      {/* Upload Section - Different inputs based on type */}
      {type === 'videos' ? (
        // YouTube Video Input for video galleries
        <YouTubeVideoInput
          onVideoAdd={handleYouTubeVideoAdd}
          label="Ajouter une vidéo YouTube"
        />
      ) : type === 'photos' ? (
        // File Upload for photo galleries
        <FileUpload
          onFileUpload={handleFileUpload}
          acceptedTypes="image/*"
          label="Télécharger des photos"
        />
      ) : (
        // Mixed gallery - both options
        <div className="mixed-gallery-inputs">
          <div className="input-tabs" style={{
            display: 'flex',
            marginBottom: '20px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <div className="tab-content">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-images"></i>
                  Télécharger des Photos
                </h4>
                <FileUpload
                  onFileUpload={handleFileUpload}
                  acceptedTypes="image/*"
                  label="Télécharger des photos"
                />
              </div>
              
              <div>
                <h4 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fab fa-youtube" style={{ color: '#ff0000' }}></i>
                  Ajouter des Vidéos YouTube
                </h4>
                <YouTubeVideoInput
                  onVideoAdd={handleYouTubeVideoAdd}
                  label="Ajouter une vidéo YouTube"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Gallery Items Management */}
      {value.length > 0 && (
        <div className="gallery-items-manager">
          <div className="gallery-items-header">
            <h4 className="gallery-items-title">Éléments de la galerie</h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className="gallery-items-count">
                {value.length} élément{value.length > 1 ? 's' : ''}
              </span>
              {value.length > 1 && (
                <button
                  type="button"
                  className="media-preview-btn"
                  onClick={selectAllItems}
                  title={selectedItems.length === value.length ? "Tout désélectionner" : "Tout sélectionner"}
                >
                  <i className={`fas ${selectedItems.length === value.length ? 'fa-times' : 'fa-check-square'}`}></i>
                  {selectedItems.length === value.length ? 'Désélectionner' : 'Sélectionner tout'}
                </button>
              )}
              {selectedItems.length > 0 && (
                <button
                  type="button"
                  className="media-preview-btn danger"
                  onClick={handleBulkDelete}
                  title={`Supprimer ${selectedItems.length} élément(s)`}
                >
                  <i className="fas fa-trash"></i>
                  Supprimer ({selectedItems.length})
                </button>
              )}
            </div>
          </div>
          
          <div className="gallery-items-grid">
            {value.map((item, index) => (
              <div 
                key={item.id || index} 
                className={`gallery-item-card ${selectedItems.includes(index) ? 'selected' : ''}`}
              >
                {/* Selection Checkbox */}
                <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 2 }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(index)}
                    onChange={() => toggleItemSelection(index)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                </div>
                
                {/* Media Preview */}
                <div style={{ position: 'relative' }}>
                  {item.data && item.type === 'image' ? (
                    // Regular uploaded image
                    <img 
                      src={item.data} 
                      alt={item.title}
                      className="gallery-item-preview"
                    />
                  ) : item.youtube_id && item.thumbnail ? (
                    // YouTube video with thumbnail
                    <div style={{ position: 'relative' }}>
                      <img 
                        src={item.thumbnail} 
                        alt={item.title}
                        className="gallery-item-preview"
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                        }}
                      />
                      {/* YouTube Play Button */}
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#ff0000',
                        fontSize: '2.5rem',
                        textShadow: '0 2px 4px rgba(0,0,0,0.7)',
                        cursor: 'pointer'
                      }}>
                        <i className="fab fa-youtube"></i>
                      </div>
                      {/* Duration badge */}
                      {item.duration && (
                        <div style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.8)',
                          color: 'white',
                          padding: '3px 6px',
                          borderRadius: '3px',
                          fontSize: '0.7rem',
                          fontWeight: '500'
                        }}>
                          {item.duration}
                        </div>
                      )}
                      {/* YouTube badge */}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        background: '#ff0000',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.6rem',
                        fontWeight: '500'
                      }}>
                        YouTube
                      </div>
                    </div>
                  ) : item.thumbnail && item.type === 'video' ? (
                    // Legacy video with thumbnail (old format)
                    <div style={{ position: 'relative' }}>
                      <img 
                        src={item.thumbnail} 
                        alt={item.title}
                        className="gallery-item-preview"
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontSize: '2rem',
                        textShadow: '0 2px 4px rgba(0,0,0,0.7)'
                      }}>
                        <i className="fas fa-play-circle"></i>
                      </div>
                      {item.duration && (
                        <div style={{
                          position: 'absolute',
                          bottom: '5px',
                          right: '5px',
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '0.7rem'
                        }}>
                          {item.duration}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Placeholder for items without media
                    <div 
                      className="gallery-item-preview"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280'
                      }}
                    >
                      <i className={item.icon || 'fas fa-file'} style={{ fontSize: '2rem' }}></i>
                    </div>
                  )}
                </div>
                
                {/* Item Info */}
                <div className="gallery-item-info">
                  <input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => handleItemUpdate(index, 'title', e.target.value)}
                    className="gallery-item-title"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      width: '100%',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                    placeholder="Titre de l'élément"
                  />
                  
                  {/* Size warning for large videos */}
                  {item.sizeWarning && (
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#f59e0b',
                      backgroundColor: '#fef3c7',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      marginBottom: '0.5rem',
                      textAlign: 'center'
                    }}>
                      {item.sizeWarning}
                    </div>
                  )}
                  
                  {/* Optimization status */}
                  {item.optimized && (
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#3b82f6',
                      backgroundColor: '#dbeafe',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      marginBottom: '0.5rem',
                      textAlign: 'center'
                    }}>
                      📁 Vidéo optimisée pour le stockage
                    </div>
                  )}
                  
                  <div className="gallery-item-meta">
                    <div className="gallery-item-type">
                      <i className={`${item.youtube_id ? 'fab fa-youtube' : item.type === 'video' ? 'fas fa-video' : 'fas fa-image'}`}></i>
                      <span>
                        {item.youtube_id ? 'YouTube' : item.type === 'video' ? 'Vidéo' : 'Image'}
                        {item.type === 'video' && item.duration && ` (${item.duration})`}
                      </span>
                    </div>
                    
                    <div className="gallery-item-actions">
                      <button
                        type="button"
                        className="gallery-item-action"
                        onClick={() => handleEditItem(index)}
                        title="Modifier cet élément"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        type="button"
                        className="gallery-item-action danger"
                        onClick={() => handleItemDelete(index)}
                        title="Supprimer cet élément"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <EditItemModal
          item={editingItem.item}
          itemIndex={editingItem.index}
          onSave={handleSaveEditedItem}
          onClose={handleCloseEditModal}
          galleryType={type}
        />
      )}
    </div>
  );
};

export default GalleryManager;
